import { getFoodSearchFiltersForOrgLocale } from "@notemeal/food/utils";
import { useLocaleContext } from "@notemeal/shared/ui/contexts/LocaleContext";
import React from "react";
import {
  Food,
  FoodGroupsDocument,
  FoodGroupsQuery,
  FoodGroupsQueryVariables,
  FullFoodGroupFragment,
  SimpleFoodsDocument,
  SimpleFoodsQuery,
  SimpleFoodsQueryVariables,
} from "../../../types";
import { IFoodGroupFood } from "../../FoodGroup/FoodTable";
import { AutocompleteQuerySearchBar } from "../../universal/AutocompleteQuerySearchBar/AutocompleteQuerySearchBar";
import MultiQuerySearchBar from "../../universal/MultiQuerySearchBar";

interface AddFoodProps {
  type: "Athlete" | "MealPlan";
  id: string;
  food: Pick<Food, "id" | "name">;
}

interface AddFoodGroupProps {
  type: "Athlete" | "MealPlan";
  id: string;
  foodGroup: FullFoodGroupFragment;
}

interface FullFoodGroupFragmentSearchBarProps {
  type: "Athlete" | "MealPlan";
  preference: "like" | "dislike";
  preferrerId: string;
  addFood: ({ type, id, food }: AddFoodProps) => void;
  addFoodGroup: ({ type, id, foodGroup }: AddFoodGroupProps) => void;
}

const FoodMultiSearchBar = ({ preference, preferrerId, addFood, addFoodGroup, type }: FullFoodGroupFragmentSearchBarProps) => {
  const positiveLabel = type === "Athlete" ? "Liked" : "Promoted";
  const negativeLabel = type === "Athlete" ? "Disliked" : "Avoided";

  const localeContext = useLocaleContext();
  const { localeFilter, dataSourceFilter } = getFoodSearchFiltersForOrgLocale(localeContext.locale);

  const labelToRenderFunctionMap = {
    Foods: () => (
      <AutocompleteQuerySearchBar<IFoodGroupFood, SimpleFoodsQuery, SimpleFoodsQueryVariables>
        query={SimpleFoodsDocument}
        label={preference === "like" ? `${positiveLabel} Foods` : `${negativeLabel} Foods`}
        placeholder=""
        getOptionLabel={o => o.name}
        getOptionsFromQueryData={data => [...data.foods]}
        getQueryOptions={searchTerm => ({
          variables: {
            searchTerm,
            limit: 10,
            localeCodes: localeFilter,
            dataSources: dataSourceFilter,
          },
        })}
        onChange={o => (o ? addFood({ type, id: preferrerId, food: { id: o.id, name: o.name } }) : null)}
        getUserFriendlyQueryErrorMessage={() => "Error searching foods, please try again."}
      />
    ),
    "Food Groups": () => (
      <AutocompleteQuerySearchBar<FullFoodGroupFragment, FoodGroupsQuery, FoodGroupsQueryVariables>
        query={FoodGroupsDocument}
        label={preference === "like" ? `${positiveLabel} Food Groups` : `${negativeLabel} Food Groups`}
        placeholder=""
        getOptionLabel={o => o.name}
        getOptionsFromQueryData={data => [...data.foodGroups]}
        getQueryOptions={searchTerm => ({
          variables: {
            searchTerm,
            limit: 100,
          },
        })}
        onChange={o => (o ? addFoodGroup({ type, id: preferrerId, foodGroup: o }) : null)}
        getUserFriendlyQueryErrorMessage={() => "Error searching food groups, please try again."}
      />
    ),
  };

  return <MultiQuerySearchBar labelToRenderFunctionMap={labelToRenderFunctionMap} initialLabel={"Food Groups"} />;
};

export default FoodMultiSearchBar;
